import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { ITranslatorService } from '../../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../../context/Contexts';
import {
  Tooltip,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  Grid,
  Typography,
  DialogContent,
  Card,
  TextField,
  CircularProgress
} from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import AddCircleTwoToneIcon from '@material-ui/icons/AddCircleTwoTone';
import { RouteComponentProps } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MUIDataTable, {
  SelectableRows,
  MUIDataTableMeta,
  MUIDataTableColumnOptions
} from 'mui-datatables';
import { MUITranslations } from '../../../../helpers/MUITableTranslations';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineTwoToneIcon from '@material-ui/icons/DeleteOutlineTwoTone';
import { GTOperationEnum, LanguageCode, RefConstants } from '../../../../helpers/Constants';
import { CloudDownloadOutlined } from '@material-ui/icons';
import FileSaver from 'file-saver';
import { ICaseService } from '../../../../services/Interfaces/ICaseService';
import { IOrganizationService } from '../../../../services/Interfaces/IOrganizationService';
import { IReferential } from '../../../../interfaces/IReferential';
import moment from 'moment';
import DamageReferentialLabel from '../../../_shared/DamageReferentialLabel';
import { IReferentialService } from '../../../../services/Interfaces/IReferentialService';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import {
  PartMapping,
  PartMappingFilterRequestDto,
  PartMappingListItemDto
} from '../../../../interfaces/Organization';
import CloudUploadOutlined from '@material-ui/icons/CloudUploadOutlined';
import NumberFormat from 'react-number-format';
import PartMappingImport from './PartMappingImport';

interface IPartMappingsState {
  newPartMapping: PartMapping;
  partMappingList: PartMappingListItemDto[];
  isLoading: boolean;
  isExporting: boolean;
  page: number;
  pageSize: number;
  count: number;
  selectedBodyTypes: IReferential[];
  selectedGTParts: IReferential[];
  cupiCodesFilter: string;
  selectedDamageTypes: IReferential[];
  selectedOperations: IReferential[];
  selectedDamageSeverities: IReferential[];
  isDialogOpen: boolean;
  newPartMappingId: number | null;
  bodyTypeList: IReferential[];
  gtPartList: IReferential[];
  damageTypeList: IReferential[];
  damageSeverityList: IReferential[];
  operationList: IReferential[];
  isImportDialogOpen: boolean;
}
type IPartMappingsProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string }>;
class PartMappings extends React.PureComponent<IPartMappingsProps, IPartMappingsState> {
  private translatorService!: ITranslatorService;
  private caseService!: ICaseService;
  private organizationService!: IOrganizationService;
  private appReferentialService!: IReferentialService;
  static contextType = ApplicationContext;
  private newPartMapping = {
    id: 0,
    organizationId: 0,
    bodyTypeId: null,
    gtPartId: null,
    cupiCodes: '',
    damageTypeId: null,
    operationId: null,
    damageSeverityId: null,
    time: null
  } as PartMapping;

  state = {
    partMappingList: [],
    isLoading: false,
    isExporting: false,
    page: 0,
    pageSize: 10,
    count: 0,
    selectedBodyTypes: [] as IReferential[],
    selectedGTParts: [] as IReferential[],
    cupiCodesFilter: '',
    selectedDamageTypes: [] as IReferential[],
    selectedOperations: [] as IReferential[],
    selectedDamageSeverities: [] as IReferential[],
    isDialogOpen: false,
    newPartMapping: this.newPartMapping,
    newPartMappingId: null,
    bodyTypeList: [] as IReferential[],
    gtPartList: [] as IReferential[],
    damageTypeList: [] as IReferential[],
    damageSeverityList: [] as IReferential[],
    operationList: [] as IReferential[],
    isImportDialogOpen: false
  } as IPartMappingsState;

  private cupiCodesFilterTimer: ReturnType<typeof setTimeout> | undefined;

  private get organizationId(): number {
    return Number.parseInt(this.props.match.params.id);
  }

  public componentDidMount() {
    this.setState(
      {
        isLoading: true
      },
      async () => {
        await this.loadData();
      }
    );
  }

  private buildFilter = (page: number, pageSize: number | null): PartMappingFilterRequestDto => {
    return {
      organizationId: this.organizationId,
      bodyTypeIds: this.state.selectedBodyTypes.map((item: IReferential) => item.id),
      gtPartIds: this.state.selectedGTParts.map((item: IReferential) => item.id),
      cupiCodes: this.state.cupiCodesFilter || null,
      damageTypeIds: this.state.selectedDamageTypes.map((item: IReferential) => item.id),
      operationIds: this.state.selectedOperations.map((item: IReferential) => item.id),
      damageSeverityIds: this.state.selectedDamageSeverities.map((item: IReferential) => item.id),
      page: page + 1,
      pageSize: pageSize,
      paginate: pageSize !== null,
      language: this.props.appState.language == LanguageCode.RO ? LanguageCode.RO : LanguageCode.EN
    } as PartMappingFilterRequestDto;
  };

  loadData = async () => {
    const { bodyTypeRef, gtPartRef, damageTypeRef, damageSeverityRef, operationRef } = RefConstants;

    try {
      const [
        bodyTypeList,
        gtPartList,
        damageTypeList,
        damageSeverityList,
        operationList,
        response
      ] = await Promise.all([
        this.appReferentialService.Get(bodyTypeRef!.baseUrl),
        this.appReferentialService.Get(gtPartRef!.baseUrl),
        this.appReferentialService.Get(damageTypeRef!.baseUrl),
        this.appReferentialService.Get(damageSeverityRef!.baseUrl),
        this.appReferentialService.Get(operationRef!.baseUrl),
        this.organizationService.GetPartMappingList(
          this.buildFilter(this.state.page, this.state.pageSize)
        )
      ]);

      this.setState({
        partMappingList: response.partMappings,
        count: response.total,
        page: response.page - 1,
        isLoading: false,
        newPartMapping: { ...this.newPartMapping, organizationId: this.organizationId },
        bodyTypeList: bodyTypeList.filter((item: IReferential) => !item.isFleet),
        gtPartList: gtPartList,
        damageTypeList: damageTypeList,
        damageSeverityList: damageSeverityList,
        operationList: operationList
      });
    } catch (error) {
      this.showLoadError(error);
    }
  };

  reloadPartMappings = async (page: number, pageSize: number) => {
    this.setState({ isLoading: true });

    try {
      const response = await this.organizationService.GetPartMappingList(
        this.buildFilter(page, pageSize)
      );

      this.setState({
        partMappingList: response.partMappings,
        count: response.total,
        page: response.page - 1,
        pageSize: pageSize,
        isLoading: false
      });
    } catch (error) {
      this.showLoadError(error);
    }
  };

  showLoadError = (error: any) => {
    const message =
      error?.response?.status === 403
        ? this.translatorService.Tranlate(
            'PART_MAPPING_FORBIDDEN_MSG',
            'Nu aveți acces la maparea pieselor acestei organizații!'
          )
        : this.translatorService.Tranlate('ERROR_MSG', 'Eroare');
    this.props.enqueueSnackbar(message, { variant: 'error' });
    this.setState({ partMappingList: [], isLoading: false });
  };

  handleBodyTypesChange = (newValue: IReferential[]) => {
    this.setState(
      { selectedBodyTypes: newValue, isLoading: true, page: 0 },
      async () => await this.reloadPartMappings(0, this.state.pageSize)
    );
  };

  handleGTPartsChange = (newValue: IReferential[]) => {
    this.setState(
      { selectedGTParts: newValue, isLoading: true, page: 0 },
      async () => await this.reloadPartMappings(0, this.state.pageSize)
    );
  };

  handleCupiCodesFilterChange = (value: string) => {
    this.setState({ cupiCodesFilter: value });

    clearTimeout(this.cupiCodesFilterTimer);
    this.cupiCodesFilterTimer = setTimeout(() => {
      this.setState(
        { isLoading: true, page: 0 },
        async () => await this.reloadPartMappings(0, this.state.pageSize)
      );
    }, 400);
  };

  handleDamageTypesChange = (newValue: IReferential[]) => {
    this.setState(
      { selectedDamageTypes: newValue, isLoading: true, page: 0 },
      async () => await this.reloadPartMappings(0, this.state.pageSize)
    );
  };

  handleOperationsChange = (newValue: IReferential[]) => {
    this.setState(
      { selectedOperations: newValue, isLoading: true, page: 0 },
      async () => await this.reloadPartMappings(0, this.state.pageSize)
    );
  };

  handleDamageSeveritiesChange = (newValue: IReferential[]) => {
    this.setState(
      { selectedDamageSeverities: newValue, isLoading: true, page: 0 },
      async () => await this.reloadPartMappings(0, this.state.pageSize)
    );
  };

  handleExport = async () => {
    this.setState({ isExporting: true });
    try {
      const blob = await this.organizationService.GetPartMappingExport(
        this.buildFilter(this.state.page, null)
      );
      FileSaver.saveAs(
        blob,
        `${this.translatorService.Tranlate('PART_MAPPING_EXPORT_FILE_NAME', 'Mapare piese')}.xlsx`
      );
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ isExporting: false });
    }
  };

  renderDamageSeverity = (damageSeverity: IReferential) => (
    <DamageReferentialLabel
      displayName={damageSeverity.displayName}
      displayDescription={damageSeverity.displayDescription}
      level={damageSeverity.level}
    />
  );

  getDamageTypeName = (damageSeverity: IReferential) =>
    this.state.damageTypeList.find((item: IReferential) => item.id === damageSeverity.dependencyId)
      ?.displayName ?? '';

  renderFilters = () => {
    // the severities follow the selected damage types; all of them when none is selected
    const damageSeverityOptions =
      this.state.selectedDamageTypes.length === 0
        ? this.state.damageSeverityList
        : this.state.damageSeverityList.filter((item: IReferential) =>
            this.state.selectedDamageTypes.some(
              (damageType: IReferential) => damageType.id === item.dependencyId
            )
          );

    return (
      <div className="mt-3">
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box mb={2}>
              <Autocomplete
                id="bodyTypeFilter"
                multiple
                options={this.state.bodyTypeList}
                value={this.state.selectedBodyTypes}
                onChange={(e: any, newValue: any) => this.handleBodyTypesChange(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="bodyTypeFilter"
                    label={this.translatorService.Tranlate(
                      'PART_MAPPING_BODY_TYPE_LABEL',
                      'Caroserie'
                    )}
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box mb={2}>
              <Autocomplete
                id="operationFilter"
                multiple
                options={this.state.operationList}
                value={this.state.selectedOperations}
                onChange={(e: any, newValue: any) => this.handleOperationsChange(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="operationFilter"
                    label={this.translatorService.Tranlate(
                      'PART_MAPPING_OPERATION_LABEL',
                      'Operație'
                    )}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box mb={2}>
              <Autocomplete
                id="gtPartFilter"
                multiple
                options={this.state.gtPartList}
                value={this.state.selectedGTParts}
                onChange={(e: any, newValue: any) => this.handleGTPartsChange(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="gtPartFilter"
                    label={this.translatorService.Tranlate('PART_MAPPING_PART_LABEL', 'Piesă')}
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box mb={2}>
              <TextField
                id="cupiCodesFilter"
                value={this.state.cupiCodesFilter}
                onChange={(e: any) => this.handleCupiCodesFilterChange(e.target.value)}
                label={this.translatorService.Tranlate(
                  'PART_MAPPING_CUPI_CODES_LABEL',
                  'Coduri CUPI'
                )}
                fullWidth
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box mb={2}>
              <Autocomplete
                id="damageTypeFilter"
                multiple
                options={this.state.damageTypeList}
                value={this.state.selectedDamageTypes}
                onChange={(e: any, newValue: any) => this.handleDamageTypesChange(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="damageTypeFilter"
                    label={this.translatorService.Tranlate(
                      'PART_MAPPING_DAMAGE_TYPE_LABEL',
                      'Tip daună'
                    )}
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box mb={2}>
              <Autocomplete
                id="damageSeverityFilter"
                multiple
                options={damageSeverityOptions}
                value={this.state.selectedDamageSeverities}
                onChange={(e: any, newValue: any) => this.handleDamageSeveritiesChange(newValue)}
                groupBy={(option: IReferential) => this.getDamageTypeName(option)}
                getOptionLabel={(option: IReferential) =>
                  `${option.displayName || ''} (${this.getDamageTypeName(option)})`
                }
                renderOption={(option: IReferential) => this.renderDamageSeverity(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="damageSeverityFilter"
                    label={this.translatorService.Tranlate(
                      'PART_MAPPING_DAMAGE_SEVERITY_LABEL',
                      'Severitate'
                    )}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </div>
    );
  };

  private handleBodyTypeChange = (newValue: IReferential | null) => {
    this.setState({
      newPartMapping: {
        ...this.state.newPartMapping,
        bodyTypeId: newValue !== null ? newValue!.id : null
      }
    });
  };

  private handleGTPartChange = (newValue: IReferential | null) => {
    this.setState({
      newPartMapping: {
        ...this.state.newPartMapping,
        gtPartId: newValue !== null ? newValue!.id : null
      }
    });
  };

  private handleCupiCodesChange = (value: string) => {
    this.setState({
      newPartMapping: {
        ...this.state.newPartMapping,
        cupiCodes: value
      }
    });
  };

  private handleDamageTypeChange = (newValue: IReferential | null) => {
    // a severity belongs to one damage type, so it resets with it
    this.setState({
      newPartMapping: {
        ...this.state.newPartMapping,
        damageTypeId: newValue !== null ? newValue!.id : null,
        damageSeverityId: null
      }
    });
  };

  private handleOperationChange = (newValue: IReferential | null) => {
    this.setState({
      newPartMapping: {
        ...this.state.newPartMapping,
        operationId: newValue !== null ? newValue!.id : null
      }
    });
  };

  private handleDamageSeverityChange = (newValue: IReferential | null) => {
    this.setState({
      newPartMapping: {
        ...this.state.newPartMapping,
        damageSeverityId: newValue !== null ? newValue!.id : null
      }
    });
  };

  private handleTimeChange = (value: string) => {
    const num = value === '' || value === undefined ? null : parseFloat(value);

    this.setState({
      newPartMapping: {
        ...this.state.newPartMapping,
        time: num !== null && !isNaN(num) ? num : null
      }
    });
  };

  private NumberFormatCustom = (numberProps: any) => {
    const { inputRef, onChange, ...other } = numberProps;
    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef}
        onValueChange={(values: any) => {
          onChange({
            target: { value: values.value },
            currentTarget: { value: values.value }
          });
        }}
        allowNegative={false}
        thousandSeparator={false}
        decimalScale={2}
      />
    );
  };

  public savePartMapping = async () => {
    const newPartMapping = { ...this.state.newPartMapping };

    try {
      const duplicateResponse = await this.organizationService.CheckPartMappingDuplicate({
        id: newPartMapping.id,
        organizationId: this.organizationId,
        bodyTypeId: newPartMapping.bodyTypeId!,
        gtPartId: newPartMapping.gtPartId!,
        damageTypeId: newPartMapping.damageTypeId!,
        operationId: newPartMapping.operationId!,
        damageSeverityId: newPartMapping.damageSeverityId
      });

      if (duplicateResponse.isDuplicate) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(
            'PART_MAPPING_EXISTS',
            'Maparea introdusa exista deja in lista'
          ),
          { variant: 'error' }
        );
        return;
      }

      if (duplicateResponse.isOperationMismatch) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(
            'PART_MAPPING_OPERATION_MISMATCH',
            'Tipul de daună are deja altă operație pe această piesă'
          ),
          { variant: 'error' }
        );
        return;
      }

      if (this.state.newPartMappingId === null) {
        await this.organizationService.AddPartMapping(newPartMapping);
      } else {
        await this.organizationService.UpdatePartMapping(newPartMapping);
      }
      await this.reloadPartMappings(this.state.page, this.state.pageSize);
      this.closeDialog();
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  public renderAddForm() {
    const { newPartMapping } = this.state;
    const required = [
      this.translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
    ];
    const findReferential = (list: IReferential[], id: number | null) =>
      list.find((item: IReferential) => item.id === id) ?? null;

    const bodyType = findReferential(this.state.bodyTypeList, newPartMapping.bodyTypeId);
    const gtPart = findReferential(this.state.gtPartList, newPartMapping.gtPartId);
    const damageType = findReferential(this.state.damageTypeList, newPartMapping.damageTypeId);
    const operation = findReferential(this.state.operationList, newPartMapping.operationId);
    // only the severities of the chosen damage type; none for the types without severities
    const damageSeverityOptions = this.state.damageSeverityList.filter(
      (item: IReferential) => item.dependencyId === newPartMapping.damageTypeId
    );
    const damageSeverity = findReferential(damageSeverityOptions, newPartMapping.damageSeverityId);
    const isRepair = operation?.code === GTOperationEnum.RE;
    // the choice of an existing line stays fixed: another choice is a new line, with a new id
    const isEdit = this.state.newPartMappingId !== null;

    return (
      <ValidatorForm onSubmit={this.savePartMapping} instantValidate={true}>
        <Box mb={2}>
          <Autocomplete
            id="bodyType"
            fullWidth
            disabled={isEdit}
            options={this.state.bodyTypeList}
            value={bodyType}
            onChange={(e: any, newValue: IReferential | null) =>
              this.handleBodyTypeChange(newValue)
            }
            getOptionLabel={(option: IReferential) => option.displayName || ''}
            renderInput={(params) => (
              <TextValidator
                {...params}
                name="bodyType"
                value={bodyType || ''}
                label={this.translatorService.Tranlate('PART_MAPPING_BODY_TYPE_LABEL', 'Caroserie')}
                fullWidth
                validators={['required']}
                errorMessages={required}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Autocomplete
            id="gtPart"
            fullWidth
            disabled={isEdit}
            options={this.state.gtPartList}
            value={gtPart}
            onChange={(e: any, newValue: IReferential | null) => this.handleGTPartChange(newValue)}
            getOptionLabel={(option: IReferential) => option.displayName || ''}
            renderInput={(params) => (
              <TextValidator
                {...params}
                name="gtPart"
                value={gtPart || ''}
                label={this.translatorService.Tranlate('PART_MAPPING_PART_LABEL', 'Piesă')}
                fullWidth
                validators={['required']}
                errorMessages={required}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <TextValidator
            fullWidth
            name="cupiCodes"
            id="cupiCodes"
            value={newPartMapping.cupiCodes || ''}
            onChange={(e: any) => this.handleCupiCodesChange(e.target.value)}
            label={this.translatorService.Tranlate('PART_MAPPING_CUPI_CODES_LABEL', 'Coduri CUPI')}
            validators={['required']}
            errorMessages={required}
          />
        </Box>

        <Box mb={2}>
          <Autocomplete
            id="damageType"
            fullWidth
            disabled={isEdit}
            options={this.state.damageTypeList}
            value={damageType}
            onChange={(e: any, newValue: IReferential | null) =>
              this.handleDamageTypeChange(newValue)
            }
            getOptionLabel={(option: IReferential) => option.displayName || ''}
            renderInput={(params) => (
              <TextValidator
                {...params}
                name="damageType"
                value={damageType || ''}
                label={this.translatorService.Tranlate(
                  'PART_MAPPING_DAMAGE_TYPE_LABEL',
                  'Tip daună'
                )}
                fullWidth
                validators={['required']}
                errorMessages={required}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Autocomplete
            id="operation"
            fullWidth
            disabled={isEdit}
            options={this.state.operationList}
            value={operation}
            onChange={(e: any, newValue: IReferential | null) =>
              this.handleOperationChange(newValue)
            }
            getOptionLabel={(option: IReferential) => option.displayName || ''}
            renderInput={(params) => (
              <TextValidator
                {...params}
                name="operation"
                value={operation || ''}
                label={this.translatorService.Tranlate('PART_MAPPING_OPERATION_LABEL', 'Operație')}
                fullWidth
                validators={['required']}
                errorMessages={required}
              />
            )}
          />
        </Box>

        {damageSeverityOptions.length > 0 ? (
          <Box mb={2}>
            <Autocomplete
              id="damageSeverity"
              fullWidth
              disabled={isEdit}
              options={damageSeverityOptions}
              value={damageSeverity}
              onChange={(e: any, newValue: IReferential | null) =>
                this.handleDamageSeverityChange(newValue)
              }
              getOptionLabel={(option: IReferential) => option.displayName || ''}
              renderOption={(option: IReferential) => this.renderDamageSeverity(option)}
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  name="damageSeverity"
                  value={damageSeverity || ''}
                  label={this.translatorService.Tranlate(
                    'PART_MAPPING_DAMAGE_SEVERITY_LABEL',
                    'Severitate'
                  )}
                  helperText={damageSeverity?.displayDescription}
                  fullWidth
                  validators={['required']}
                  errorMessages={required}
                />
              )}
            />
          </Box>
        ) : null}

        <Box mb={2}>
          <TextValidator
            fullWidth
            name="time"
            id="time"
            value={newPartMapping.time ?? ''}
            onChange={(e: any) => this.handleTimeChange(e.target.value)}
            label={this.translatorService.Tranlate('PART_MAPPING_TIME_LABEL', 'Timp (ore)')}
            // required only on RE, like the import
            validators={isRepair ? ['required', 'minFloat:0.01'] : []}
            errorMessages={
              isRepair
                ? [
                    ...required,
                    this.translatorService.Tranlate(
                      'PART_MAPPING_TIME_POSITIVE',
                      'Timpul trebuie să fie mai mare decât 0'
                    )
                  ]
                : []
            }
            InputProps={{
              inputComponent: this.NumberFormatCustom,
              style: { fontWeight: 500 }
            }}
          />
        </Box>

        <div className="text-right">
          <Button className="m-2" variant="outlined" color="default" onClick={this.closeDialog}>
            {this.translatorService.Tranlate('EDIT_ORGANIZATION_CANCEL_BTN', 'Anuleaza')}
          </Button>
          <Button className="m-2" variant="contained" color="secondary" type="submit">
            {this.translatorService.Tranlate('EDIT_ORGANIZATION_SAVE_BTN', 'Salveaza')}
          </Button>
        </div>
      </ValidatorForm>
    );
  }

  getColumns = () => {
    const columns = [
      {
        name: 'id',
        options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
      },
      {
        name: 'bodyType',
        label: this.translatorService.Tranlate('PART_MAPPING_BODY_TYPE_LABEL', 'Caroserie'),
        options: {
          sort: true,
          customSort: (a: IReferential, b: IReferential) =>
            (a.displayName ?? '').localeCompare(b.displayName ?? ''),
          customBodyRender: (value: IReferential) => (value ? value.displayName : '')
        } as MUIDataTableColumnOptions
      },
      {
        name: 'gtPart',
        label: this.translatorService.Tranlate('PART_MAPPING_PART_LABEL', 'Piesă'),
        options: {
          sort: true,
          customSort: (a: IReferential, b: IReferential) =>
            (a.displayName ?? '').localeCompare(b.displayName ?? ''),
          customBodyRender: (value: IReferential) => (value ? value.displayName : '')
        } as MUIDataTableColumnOptions
      },
      {
        name: 'cupiCodes',
        label: this.translatorService.Tranlate('PART_MAPPING_CUPI_CODES_LABEL', 'Coduri CUPI')
      },
      {
        name: 'damageType',
        label: this.translatorService.Tranlate('PART_MAPPING_DAMAGE_TYPE_LABEL', 'Tip daună'),
        options: {
          setCellProps: () => ({ style: { minWidth: 280 } }),
          customBodyRender: (value: IReferential) =>
            value ? (
              <DamageReferentialLabel
                displayName={value.displayName}
                displayDescription={value.displayDescription}
              />
            ) : (
              ''
            )
        } as MUIDataTableColumnOptions
      },
      {
        name: 'operation',
        label: this.translatorService.Tranlate('PART_MAPPING_OPERATION_LABEL', 'Operație'),
        options: {
          customBodyRender: (value: IReferential) => (value ? value.displayName : '')
        } as MUIDataTableColumnOptions
      },
      {
        name: 'damageSeverity',
        label: this.translatorService.Tranlate('PART_MAPPING_DAMAGE_SEVERITY_LABEL', 'Severitate'),
        options: {
          setCellProps: () => ({ style: { minWidth: 200, maxWidth: 240 } }),
          customBodyRender: (value: IReferential) => (value ? this.renderDamageSeverity(value) : '')
        } as MUIDataTableColumnOptions
      },
      {
        name: 'time',
        label: this.translatorService.Tranlate('PART_MAPPING_TIME_LABEL', 'Timp (ore)'),
        options: {
          customBodyRender: (value: number | null) => (value != null ? String(value) : '')
        } as MUIDataTableColumnOptions
      },
      {
        name: 'dateModification',
        label: this.translatorService.Tranlate('USERS_DATE_MODIFICATION_HEADER', 'Data Modificare'),
        options: {
          filter: false,
          sortDirection: 'desc',
          customBodyRender: (value: Date) =>
            value ? moment.utc(value).local().format(this.props.appState.longDateFormat) : ''
        } as MUIDataTableColumnOptions
      },
      {
        name: '',
        label: this.translatorService.Tranlate('PART_MAPPING_ACTIONS_LABEL', 'Acțiuni'),
        options: {
          viewColumns: false,
          filter: false,
          sort: false,
          setCellHeaderProps: () => ({ align: 'center' }),
          setCellProps: () => ({ align: 'center' }),
          customBodyRender: (value: boolean, tableMeta: MUIDataTableMeta) => {
            return (
              <>
                <IconButton
                  aria-label="edit"
                  color="primary"
                  className="text-primary"
                  size="small"
                  onClick={(e) => this.newPartMappingPopup(tableMeta.rowData[0])}
                >
                  <EditOutlinedIcon />
                </IconButton>
                <IconButton
                  aria-label="delete"
                  color="inherit"
                  className="text-danger"
                  size="small"
                  onClick={(e) => this.deletePartMapping(e, tableMeta)}
                >
                  <DeleteOutlineTwoToneIcon />
                </IconButton>
              </>
            );
          }
        }
      }
    ];
    return columns;
  };

  deletePartMapping = async (event: any, tableMeta: MUIDataTableMeta) => {
    try {
      const id = tableMeta.rowData[0];
      await this.organizationService.DeletePartMapping(this.organizationId, id);
      await this.reloadPartMappings(this.state.page, this.state.pageSize);
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  newPartMappingPopup = async (partMappingId: number | null) => {
    let newPartMapping: PartMapping;
    if (partMappingId == null) {
      newPartMapping = { ...this.state.newPartMapping } as PartMapping;
    } else {
      const item = this.state.partMappingList.find(
        (partMapping: PartMappingListItemDto) => partMapping.id === partMappingId
      )!;
      newPartMapping = {
        id: item.id,
        organizationId: item.organizationId,
        bodyTypeId: item.bodyTypeId,
        gtPartId: item.gtPartId,
        cupiCodes: item.cupiCodes,
        damageTypeId: item.damageTypeId,
        operationId: item.operationId,
        damageSeverityId: item.damageSeverityId,
        time: item.time
      };
    }
    this.setState({
      isDialogOpen: true,
      newPartMapping: newPartMapping,
      newPartMappingId: partMappingId
    });
  };

  closeDialog = async () => {
    this.setState({
      isDialogOpen: false,
      newPartMapping: { ...this.newPartMapping, organizationId: this.organizationId }
    });
  };

  openImportDialog = async () => {
    this.setState({ isImportDialogOpen: true });
  };

  closeImportDialog = async () => {
    this.setState(
      {
        isImportDialogOpen: false,
        isLoading: true
      },
      async () => {
        await this.reloadPartMappings(0, this.state.pageSize);
      }
    );
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.caseService = (this.context as AppContext).caseService;
    this.organizationService = (this.context as AppContext).organizationService;
    this.appReferentialService = (this.context as AppContext).referentialService;
    const options = {
      filter: false,
      search: false,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      sort: false,
      viewColumns: false,
      elevation: 0,
      serverSide: true,
      count: this.state.count,
      rowsPerPage: this.state.pageSize,
      page: this.state.page,
      onTableChange: async (action: any, tableState: any) => {
        switch (action) {
          case 'changePage':
            await this.reloadPartMappings(tableState.page, tableState.rowsPerPage);
            break;
          case 'changeRowsPerPage':
            await this.reloadPartMappings(tableState.page, tableState.rowsPerPage);
            break;
          default:
        }
      },
      customToolbar: () => {
        return (
          <div>
            <Tooltip title={this.translatorService.Tranlate('Add', 'Adauga')}>
              <IconButton
                aria-label="add"
                color="secondary"
                className="m-2"
                onClick={(e) => this.newPartMappingPopup(null)}
              >
                <AddCircleTwoToneIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={this.translatorService.Tranlate('Import', 'Importa')}>
              <IconButton
                aria-label="import"
                color="primary"
                className="m-2"
                onClick={(e) => this.openImportDialog()}
              >
                <CloudUploadOutlined />
              </IconButton>
            </Tooltip>
            {this.state.isExporting ? (
              <CircularProgress
                size={20}
                color="primary"
                style={{ verticalAlign: 'middle', marginRight: 4 }}
              />
            ) : null}
            <Tooltip title={this.translatorService.Tranlate('Export', 'Exporta')}>
              <span>
                <IconButton
                  aria-label="export"
                  color="primary"
                  className="m-2"
                  disabled={this.state.isExporting}
                  onClick={(e) => this.handleExport()}
                >
                  <CloudDownloadOutlined />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        );
      }
    };

    return (
      <Fragment>
        <Card>
          <div
            style={{
              backgroundColor: 'var(--primary)',
              fontWeight: 500,
              borderBottom: '4px solid var(--primary-light)'
            }}
            className="card-header text-white text-center"
          >
            <span className="mb-0 py-2">
              {this.translatorService.Tranlate('ORGANIZATIONS_TAB_PART_MAPPING', 'Mapare piese')}
            </span>
          </div>
          <div className="ml-3 mr-3">{this.renderFilters()}</div>
          <Box m={1} p={1}>
            <div className="d-flex flex-row text-center flex-wrap justify-content-center">
              <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
            </div>
            {!this.state.isLoading ? (
              <MUIDataTable
                title={''}
                data={this.state.partMappingList}
                columns={this.getColumns()}
                options={options}
              />
            ) : null}
          </Box>
        </Card>
        {this.state.isDialogOpen ? (
          <Dialog
            onClose={this.closeDialog}
            aria-labelledby="customized-dialog-title"
            open={this.state.isDialogOpen}
            fullWidth={true}
            maxWidth={'sm'}
          >
            <DialogTitle id="customized-dialog-title">
              <Grid container>
                <Grid item xs={11}>
                  <Typography variant="h4">
                    {!this.state.newPartMappingId
                      ? this.translatorService.Tranlate(
                          'PART_MAPPING_ADD_MODAL_TITLE',
                          'Adaugă mapare piesă'
                        )
                      : this.translatorService.Tranlate(
                          'PART_MAPPING_UPDATE_MODAL_TITLE',
                          'Actualizează mapare piesă'
                        )}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <IconButton aria-label="close" onClick={this.closeDialog} size={'small'}>
                    <CloseIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent dividers>{this.renderAddForm()}</DialogContent>
          </Dialog>
        ) : null}
        {this.state.isImportDialogOpen ? (
          <PartMappingImport
            open={this.state.isImportDialogOpen}
            organizationId={this.organizationId}
            translatorService={this.translatorService}
            organizationService={this.organizationService}
            caseService={this.caseService}
            onClose={this.closeImportDialog}
          />
        ) : (
          ''
        )}
      </Fragment>
    );
  }
}
export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(PartMappings as any));
